import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import { tokens } from '../../theme';
import { formatGerman } from '../../helpers/chartUtils';

type CoregErrorBucket = 'new' | 'reactivated' | 'dup' | 'dup_tag_update' | 'error' | 'unknown';

interface CoregErrorRow {
  destination: 'mailchimp' | 'vnr';
  destination_label: string;
  result_code: number;
  bucket: CoregErrorBucket;
  code_label: string;
  message_template: string;
  count: number;
}

interface CoregErrorsModalProps {
  open: boolean;
  onClose: () => void;
  portalLabel: string;
  queryParams: {
    startDate: string;
    endDate: string;
    portals: string;
  } | null;
}

const bucketLabel: Record<CoregErrorBucket, string> = {
  new: 'Neu',
  reactivated: 'Reaktiviert',
  dup: 'Dublette',
  dup_tag_update: 'Dup. + Tag-Update',
  error: 'Fehler',
  unknown: 'Unklar',
};

const bucketChipProps: Record<
  CoregErrorBucket,
  {
    color: 'success' | 'warning' | 'error' | 'default' | 'info' | 'primary';
    variant?: 'filled' | 'outlined';
  }
> = {
  new: { color: 'success' },
  reactivated: { color: 'info' },
  dup: { color: 'warning' },
  dup_tag_update: { color: 'warning', variant: 'outlined' },
  error: { color: 'error' },
  unknown: { color: 'default' },
};

// Buckets treated as "active" response (not error/unknown) — hidden when the
// "Nur Fehler & Unklare" switch is ON.
const errorBuckets: CoregErrorBucket[] = ['error', 'unknown'];

const CoregErrorsModal: React.FC<CoregErrorsModalProps> = ({
  open,
  onClose,
  portalLabel,
  queryParams,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [rows, setRows] = useState<CoregErrorRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorsOnly, setErrorsOnly] = useState(true);

  useEffect(() => {
    if (!open || !queryParams) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const url = (process.env.REACT_APP_API_BASE_URL || '') + '/api/coreg-efficiency/errors';
        const response = await axios.get<CoregErrorRow[]>(url, {
          params: {
            startDate: queryParams.startDate,
            endDate: queryParams.endDate,
            portals: queryParams.portals,
          },
        });
        if (cancelled) return;
        setRows(Array.isArray(response.data) ? response.data : []);
      } catch (err) {
        console.error('Failed to load co-reg errors:', err);
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [open, queryParams]);

  const filtered = useMemo(() => {
    if (!errorsOnly) return rows;
    return rows.filter((r) => errorBuckets.includes(r.bucket));
  }, [rows, errorsOnly]);

  const totalCount = filtered.reduce((sum, r) => sum + r.count, 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h6" fontWeight="bold">
            Detail-Ansicht: {portalLabel}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {
              'Aufschlüsselung aller Export-Antworten je Ziel (pro Versuch, nicht dedupliziert). Die Gesamtzahl kann von der Zeile abweichen, wenn „Pro E-Mail · Tag" aktiv ist.'
            }
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ backgroundColor: colors.primary[400] }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="body2" color="text.secondary">
            {formatGerman.format(totalCount)} Export-Antworten · {filtered.length} Gruppen
          </Typography>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={errorsOnly}
                onChange={(e) => setErrorsOnly(e.target.checked)}
              />
            }
            label="Nur Fehler & Unklare"
          />
        </Box>
        {loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : filtered.length === 0 ? (
          <Box textAlign="center" py={4}>
            <Typography color="text.secondary">Keine Einträge.</Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      backgroundColor: '#e0e0e0 !important',
                      color: 'black',
                      fontWeight: 'bold',
                    }}
                  >
                    Ziel
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: '#e0e0e0 !important',
                      color: 'black',
                      fontWeight: 'bold',
                    }}
                  >
                    Code
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: '#e0e0e0 !important',
                      color: 'black',
                      fontWeight: 'bold',
                    }}
                  >
                    Bucket
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: '#e0e0e0 !important',
                      color: 'black',
                      fontWeight: 'bold',
                    }}
                  >
                    Bedeutung
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: '#e0e0e0 !important',
                      color: 'black',
                      fontWeight: 'bold',
                    }}
                  >
                    Nachricht
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      backgroundColor: '#e0e0e0 !important',
                      color: 'black',
                      fontWeight: 'bold',
                    }}
                  >
                    Anzahl
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((row, idx) => (
                  <TableRow
                    key={`${row.destination}-${row.result_code}-${idx}`}
                    sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}
                  >
                    <TableCell sx={{ color: 'black' }}>{row.destination_label}</TableCell>
                    <TableCell sx={{ color: 'black', fontFamily: 'monospace' }}>
                      {row.result_code}
                    </TableCell>
                    <TableCell sx={{ color: 'black' }}>
                      <Chip
                        label={bucketLabel[row.bucket]}
                        color={bucketChipProps[row.bucket].color}
                        variant={bucketChipProps[row.bucket].variant ?? 'filled'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ color: 'black' }}>{row.code_label}</TableCell>
                    <TableCell sx={{ color: 'black', maxWidth: 600, wordBreak: 'break-word' }}>
                      {row.message_template || (
                        <span style={{ color: '#888', fontStyle: 'italic' }}>
                          (keine Nachricht)
                        </span>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'black', fontWeight: 600 }}>
                      {formatGerman.format(row.count)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CoregErrorsModal;
